import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, ChevronRight, MessageSquare, Search } from 'lucide-react'
import { toast } from 'sonner'
import {
  useAddWorkOrderCommentMutation,
  useCompleteWorkOrderMutation,
  useGetWorkOrderQuery,
  useListWorkOrdersQuery,
  workOrderSupportsWrites,
  type WorkOrder,
  type WorkOrderListStatus,
  type WorkOrderPriority
} from '@axilon-apps/domains-ontology-ui-state'
import { useChatAppContext } from '../../chat-app-context'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { cn } from '../lib/utils'
import {
  buildWorkOrdersAppContextEntries,
  WORK_ORDERS_SUPERSEDED_PARAMS
} from './work-orders-app-context'
import { useTechnicianName } from '../lib/technician-prefs'
import {
  firstInstructionLine,
  relativeTimeAgo,
  relativeTimeAgoLong,
  sortWorkOrdersNewestFirst,
  sortWorkOrdersWorstFirst
} from '../lib/work-order-format'

const PRIORITIES: WorkOrderPriority[] = ['critical', 'high', 'medium', 'low']
const STATUS_TABS: { value: WorkOrderListStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' }
]

/** `all` = omit `source` query (federated merge). Other values map to proxy `?source=`. */
export type SourceFilter = 'all' | 'internal' | 'servicenow'
const SOURCE_TABS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'internal', label: 'Internal' },
  { value: 'servicenow', label: 'ServiceNow' }
]

export type SortMode = 'priority' | 'newest'

/**
 * Technician work-order inbox — expandable rows matching the design mock,
 * wired to proxy `/api/v1/work-orders*` via RTK.
 *
 * Collapsed row: priority + instruction + equipment/location + meta.
 * Expanded panel: comments only.
 */
export function WorkOrdersPage() {
  const { id: routeId } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const status = (searchParams.get('status') as WorkOrderListStatus | null) ?? 'open'
  const q = searchParams.get('q') ?? ''
  const priorityParam = searchParams.get('priority') ?? ''
  const sort = (searchParams.get('sort') as SortMode | null) ?? 'priority'
  const sourceParam = searchParams.get('source')
  const source: SourceFilter =
    sourceParam === 'internal' || sourceParam === 'servicenow' ? sourceParam : 'all'
  const selectedPriorities = useMemo(
    () =>
      priorityParam
        .split(',')
        .map((p) => p.trim())
        .filter((p): p is WorkOrderPriority => PRIORITIES.includes(p as WorkOrderPriority)),
    [priorityParam]
  )

  const listFilters = useMemo(
    () => ({
      status,
      q: q.trim() || undefined,
      priority: selectedPriorities.length > 0 ? selectedPriorities : undefined,
      source: source === 'all' ? undefined : source,
      page_size: 100
    }),
    [status, q, selectedPriorities, source]
  )

  const { data, isLoading, isError, error } = useListWorkOrdersQuery(listFilters, {
    pollingInterval: 30_000
  })

  const orders = useMemo(() => {
    const list = data?.work_orders ?? []
    return sort === 'newest' ? sortWorkOrdersNewestFirst(list) : sortWorkOrdersWorstFirst(list)
  }, [data?.work_orders, sort])

  const openCount = useMemo(
    () => (data?.work_orders ?? []).filter((wo) => wo.status !== 'completed').length,
    [data?.work_orders]
  )

  const expandedId = routeId ? decodeURIComponent(routeId) : null
  const expandedOrder = expandedId === null ? undefined : orders.find((wo) => wo.id === expandedId)

  useChatAppContext(
    buildWorkOrdersAppContextEntries({
      expanded:
        expandedOrder === undefined
          ? undefined
          : {
              title: firstInstructionLine(expandedOrder.instruction),
              status: expandedOrder.status,
              priority: expandedOrder.priority,
              id: expandedOrder.id
            },
      status,
      priorities: selectedPriorities,
      source,
      sort,
      search: q,
      listedCount: orders.length
    }),
    WORK_ORDERS_SUPERSEDED_PARAMS
  )

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null || value === '') next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const togglePriority = (p: WorkOrderPriority) => {
    const next = selectedPriorities.includes(p)
      ? selectedPriorities.filter((x) => x !== p)
      : [...selectedPriorities, p]
    setParam('priority', next.length > 0 ? next.join(',') : null)
  }

  const toggleExpand = (wo: WorkOrder) => {
    if (expandedId === wo.id) {
      navigate({ pathname: '/', search: searchParams.toString() })
      return
    }
    navigate({
      pathname: `/work-orders/${encodeURIComponent(wo.id)}`,
      search: searchParams.toString()
    })
  }

  return (
    <div className="tv-scroll">
      <div className="tv-page">
        <header className="tv-page-header">
          <div>
            <h1 className="tv-page-title">
              Work Orders
              <Badge variant="secondary" className="ml-2 font-normal">
                {status === 'open' ? `${openCount} open` : `${orders.length}`}
              </Badge>
            </h1>
            <p className="tv-page-sub">
              Tasks assigned to the floor, worst-first. Complete one, or leave a note on why it
              can&apos;t be done.
            </p>
          </div>
        </header>

        {(data?.source_errors?.length ?? 0) > 0 && (
          <div className="tv-banner" role="status">
            <AlertTriangle className="size-4 shrink-0" />
            <div>
              <div className="font-medium">Some sources are unavailable</div>
              <ul className="mt-1 list-disc pl-4 text-12">
                {(data?.source_errors ?? []).map((err) => (
                  <li key={err.source}>
                    {err.source}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="tv-filters">
          <div className="tv-search">
            <Search className="size-3.5 shrink-0 opacity-50" />
            <Input
              value={q}
              onChange={(e) => setParam('q', e.target.value || null)}
              placeholder="Search work orders"
              className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>

          <div className="tv-seg" role="tablist" aria-label="Status">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={status === tab.value}
                className={cn(status === tab.value && 'active')}
                onClick={() => setParam('status', tab.value === 'open' ? null : tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tv-seg" role="tablist" aria-label="Source">
            {SOURCE_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={source === tab.value}
                className={cn(source === tab.value && 'active')}
                onClick={() => setParam('source', tab.value === 'all' ? null : tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tv-priority-chips">
            {PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                className={cn('tv-sev', `sev-${p}`, selectedPriorities.includes(p) && 'selected')}
                onClick={() => togglePriority(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="tv-sort">
            <span className="tv-muted text-11">Sort</span>
            <div className="tv-seg">
              <button
                type="button"
                className={cn(sort === 'priority' && 'active')}
                onClick={() => setParam('sort', null)}
              >
                Priority
              </button>
              <button
                type="button"
                className={cn(sort === 'newest' && 'active')}
                onClick={() => setParam('sort', 'newest')}
              >
                Newest
              </button>
            </div>
          </div>
        </div>

        {isLoading && <div className="tv-empty">Loading work orders…</div>}
        {isError && (
          <div className="tv-empty text-destructive">
            Failed to load work orders
            {error && 'status' in error ? ` (${String(error.status)})` : ''}.
          </div>
        )}
        {!isLoading && !isError && orders.length === 0 && (
          <div className="tv-empty">No work orders match these filters.</div>
        )}

        {orders.length > 0 && (
          <div className="tv-list">
            {orders.map((wo) => (
              <WorkOrderRow
                key={wo.id}
                wo={wo}
                expanded={expandedId === wo.id}
                onToggle={() => toggleExpand(wo)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function equipmentLine(wo: WorkOrder): string {
  return [wo.equipment, wo.location].filter((part) => part && part.trim().length > 0).join(' · ')
}

function WorkOrderRow({
  wo,
  expanded,
  onToggle
}: {
  wo: WorkOrder
  expanded: boolean
  onToggle: () => void
}) {
  const canWrite = workOrderSupportsWrites(wo)
  const completed = wo.status === 'completed'
  const techName = useTechnicianName()
  const [completeWo, completeState] = useCompleteWorkOrderMutation()
  const equip = equipmentLine(wo)

  const doComplete = async (event: MouseEvent) => {
    event.stopPropagation()
    try {
      await completeWo({ id: wo.id, body: { completed_by: techName } }).unwrap()
      toast.success('Work order marked complete')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete work order')
    }
  }

  return (
    <div className={cn('tv-row-wrap', expanded && 'expanded')}>
      <button type="button" className="tv-row" onClick={onToggle}>
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        <span className={cn('tv-sev', `sev-${wo.priority}`)}>{wo.priority}</span>
        <div className="tv-row-title">
          <span className="tv-instr">{firstInstructionLine(wo.instruction)}</span>
          {equip ? <span className="tv-row-equip">{equip}</span> : null}
        </div>
        {(wo.comment_count ?? wo.comments?.length ?? 0) > 0 && (
          <span className="tv-comment-count">
            <MessageSquare className="size-3" />
            {wo.comment_count ?? wo.comments?.length}
          </span>
        )}
        <span className="tv-ago">{relativeTimeAgo(wo.created_at)}</span>
        <span className="tv-status-slot">
          {canWrite && !completed ? (
            <button
              type="button"
              className="tv-status-action"
              disabled={completeState.isLoading}
              onClick={(event) => void doComplete(event)}
            >
              <Check className="size-3.5" />
              Complete
            </button>
          ) : completed ? (
            <span className="tv-pill-done">
              <Check className="size-3.5" strokeWidth={2.5} />
              Complete
            </span>
          ) : (
            <span className="tv-pill-open">Open</span>
          )}
        </span>
      </button>

      {expanded && <WorkOrderComments woId={wo.id} listWo={wo} canWrite={canWrite} />}
    </div>
  )
}

function WorkOrderComments({
  woId,
  listWo,
  canWrite
}: {
  woId: string
  listWo: WorkOrder
  canWrite: boolean
}) {
  const { data: detail, isFetching } = useGetWorkOrderQuery(woId)
  const wo = detail ?? listWo
  const techName = useTechnicianName()
  const [addComment, commentState] = useAddWorkOrderCommentMutation()
  const [commentBody, setCommentBody] = useState('')

  const doComment = async () => {
    const body = commentBody.trim()
    if (!body) return
    const previous = commentBody
    setCommentBody('')
    try {
      await addComment({ id: woId, body: { author: techName, body } }).unwrap()
    } catch (err) {
      setCommentBody(previous)
      toast.error(err instanceof Error ? err.message : 'Failed to add comment')
    }
  }

  return (
    <div className="tv-expand">
      <div className="tv-comments-label">Comments</div>
      <div className="tv-comments">
        {isFetching && !detail && <div className="tv-muted text-13">Loading…</div>}
        {(wo.comments ?? []).map((c, i) => (
          <div key={`${c.author}-${c.created_at}-${i}`} className="tv-comment">
            <div className="tv-comment-meta">
              <span className="font-semibold">{c.author}</span>
              <span className="tv-muted text-11">{relativeTimeAgoLong(c.created_at)}</span>
            </div>
            <div className="text-13 leading-relaxed text-muted-foreground">{c.body}</div>
          </div>
        ))}
        {!isFetching && (wo.comments?.length ?? 0) === 0 && (
          <div className="tv-muted text-13">No comments yet.</div>
        )}
      </div>

      {canWrite && (
        <div className="tv-comment-composer">
          <Textarea
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            placeholder="Leave feedback, note why this can't be completed, or recommend a change…"
            rows={3}
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void doComment()}
              disabled={commentState.isLoading || !commentBody.trim()}
            >
              Add comment
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
